import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FileText, CheckCircle, Play, AlertCircle } from 'lucide-react';
import { useChatStore } from '@/stores/chatStore';
import type { Artifact } from '@/types';

interface PlanArtifactProps {
  artifact: Artifact;
}

interface PlanSummary {
  stageCount?: number;
  actionCount?: number;
  estimatedApiCalls?: number;
  planId?: string;
  sessionId?: string;
}

export function PlanArtifact({ artifact }: PlanArtifactProps) {
  const { approvePlan, planningSession, isLoading } = useChatStore();

  // Try to parse the content as JSON plan summary
  let planSummary: PlanSummary | null = null;
  try {
    if (artifact.content.startsWith('{')) {
      planSummary = JSON.parse(artifact.content);
    }
  } catch {
    // Not JSON, render as markdown
  }

  // If we have a JSON plan summary, render it as an interactive card
  if (planSummary) {
    const canApprove = planningSession?.state === 'awaiting_approval';

    return (
      <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-purple-100 dark:bg-purple-800 p-2">
            <FileText className="h-5 w-5 text-purple-600 dark:text-purple-300" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-purple-900 dark:text-purple-100">
              {artifact.title ?? 'Implementation Plan'}
            </h3>
            <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-purple-600 dark:text-purple-400 text-xs uppercase tracking-wide">
                  Stages
                </div>
                <div className="font-semibold text-purple-900 dark:text-purple-100">
                  {planSummary.stageCount ?? 1}
                </div>
              </div>
              <div>
                <div className="text-purple-600 dark:text-purple-400 text-xs uppercase tracking-wide">
                  Actions
                </div>
                <div className="font-semibold text-purple-900 dark:text-purple-100">
                  {planSummary.actionCount ?? 0}
                </div>
              </div>
              <div>
                <div className="text-purple-600 dark:text-purple-400 text-xs uppercase tracking-wide">
                  API Calls
                </div>
                <div className="font-semibold text-purple-900 dark:text-purple-100">
                  ~{planSummary.estimatedApiCalls ?? 0}
                </div>
              </div>
            </div>

            {canApprove && (
              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={() => approvePlan()}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
                >
                  <CheckCircle className="h-4 w-4" />
                  Approve Plan
                </button>
                <button
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 rounded-lg border border-purple-300 dark:border-purple-700 px-4 py-2 text-sm font-medium text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-800/50 disabled:opacity-50"
                >
                  View Details
                </button>
              </div>
            )}

            {planningSession?.state === 'awaiting_confirmation' && (
              <div className="mt-4 flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400">
                <AlertCircle className="h-4 w-4" />
                Awaiting confirmation to execute
              </div>
            )}

            {planningSession?.state === 'executing' && (
              <div className="mt-4 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <Play className="h-4 w-4 animate-pulse" />
                Execution in progress...
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Otherwise render as markdown
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <h3 className="mb-2 font-medium text-gray-900 dark:text-gray-100">
        {artifact.title ?? 'Implementation Plan'}
      </h3>
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {artifact.content}
        </ReactMarkdown>
      </div>
    </div>
  );
}
